import * as React from 'react';
import PropTypes from 'prop-types';
import { useTheme } from '@mui/material/styles';
import AppBar from '@mui/material/AppBar';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import { useState, useEffect, useRef } from 'react';
import { useContent } from '../../../../context/ContentContext';
import Chip from '@mui/material/Chip';
import Skeleton from '@mui/material/Skeleton'; // Add Skeleton component

function TabPanel(props) {
    const { children, value, index, ...other } = props;

    return (
        <div
            role="tabpanel"
            hidden={value !== index}
            id={`full-width-tabpanel-${index}`}
            aria-labelledby={`full-width-tab-${index}`}
            {...other}
        >
            {value === index && (
                <Box sx={{ p: 3 }}>
                    {children}
                </Box>
            )}
        </div>
    );
}

TabPanel.propTypes = {
    children: PropTypes.node,
    index: PropTypes.number.isRequired,
    value: PropTypes.number.isRequired,
};

function a11yProps(index) {
    return {
        id: `full-width-tab-${index}`,
        'aria-controls': `full-width-tabpanel-${index}`,
    };
}

export default function TransaksiHistory({ type, setType, startDate, endDate }) {
    const formatCurrency = (value) => `Rp. ${new Intl.NumberFormat('id-ID').format(value || 0)}`;
    const { fetchHistoryData, HistoryData, loading, error } = useContent();
    const theme = useTheme();
    const [value, setValue] = useState(0);
    const [filteredData, setFilteredData] = useState([]);
    const tabsRef = useRef(null);

    const handleChange = (event, newValue) => {
        setValue(newValue);
        let filterType = "semua";
        if (newValue === 1) filterType = "hasil";
        if (newValue === 2) filterType = "penarikan";
        setType(filterType);
    };

    useEffect(() => {
        if (startDate && endDate) {
            fetchHistoryData(startDate, endDate, type);
        }
    }, [startDate, endDate, type]);

    useEffect(() => {
        if (type === "semua") {
            setFilteredData(HistoryData);
        } else if (type === "hasil") {
            setFilteredData(HistoryData.filter((item) => item.trx_type === 2));
        } else if (type === "penarikan") {
            setFilteredData(HistoryData.filter((item) => item.trx_type === 1));
        }
    }, [HistoryData, type]);

    // Scroll to the top of the tab panel when component is mounted
    useEffect(() => {
        if (tabsRef.current) {
            tabsRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
    }, []);

    const renderStatus = (status) => {
        let label = "Unknown";
        let color = "default"; // Default MUI chip color
        let customStyles = {}


        if (status === 0) {
            label = "Requested";
            customStyles = {
                backgroundColor: "#FFD700", // Yellow background (gold color)
                color: "#000",             // Black text for contrast
            };
        } else if (status === 1) {
            label = "Approved";
            color = "success"; // Green chip for approved
        } else if (status === 2) {
            label = "Declined";
            color = "error"; // Red chip for declined
        }

        return <Chip label={label} color={color} size="small" />;
    };

    const renderTable = () => (
        <Box>
            {loading ? (
                <Box>
                    <Skeleton variant="rectangular" width="100%" height={50} sx={{ mb: 1 }} />
                    <Skeleton variant="rectangular" width="100%" height={100} sx={{ mb: 1 }} />
                    <Skeleton variant="rectangular" width="100%" height={100} sx={{ mb: 1 }} />
                    <Skeleton variant="rectangular" width="100%" height={100} sx={{ mb: 1 }} />
                </Box>
            ) : error ? (
                <Typography color="error">Error loading data. Please try again later.</Typography>
            ) : filteredData.length > 0 ? (
                filteredData.map((item) => {
                    const trxType = item.trx_type === 2 ? 'Hasil Komisi Referral' : 'Penarikan Saldo Penghasilan';
                    const formattedAmount = formatCurrency(item.amount);
                    const formattedBalance = formatCurrency(item.sisa_saldo);
                    const formattedDate = new Date(item.created_at).toLocaleString('id-ID', {
                        weekday: 'long',
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric',
                        hour: 'numeric',
                        minute: 'numeric',
                        second: 'numeric',
                    });

                    const amountColor = item.trx_type === 2 ? 'green' : 'red'; // Set color based on trx_type

                    return (
                        <Box
                            key={item.id}
                            sx={{
                                border: "1px solid #ccc",
                                borderRadius: "8px",
                                p: 2,
                                mb: 2,
                            }}
                        >
                            <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                                {trxType}
                            </Typography>
                            {/* Updated to use the renderStatus function */}
                            {renderStatus(item.status)}
                            <Typography
                                variant="body1"
                                sx={{
                                    color: amountColor, // Apply the conditional color
                                }}
                            >
                                {formattedAmount}
                            </Typography>
                            <Typography variant="body1">
                                {formattedDate}
                            </Typography>
                            <Typography variant="body1">
                                <strong>Sisa Saldo</strong> {formattedBalance}
                            </Typography>
                            {item.notes && (
                                <Typography variant="body1">
                                    <strong>Notes</strong> {item.notes || "N/A"}
                                </Typography>
                            )}
                        </Box>
                    );
                })
            ) : (
                <Typography>No data available.</Typography>
            )}
        </Box>
    );

    return (
        <Box sx={{
            bgcolor: 'background.paper',
            width: {
                xs: '88vw',  // On mobile, use 100% of the viewport width
                sm: '100%',   // On tablet and larger, use full width
                md: '100vw',
                lg: '70vw'  // On desktop, use 80% width
            },
        }}>
            <AppBar position="sticky" sx={{
                borderTopLeftRadius: '12px',
                borderTopRightRadius: '12px',
                top: 0, // Ensure it sticks to the top
                zIndex: 1, // Ensure it stays above content when scrolling
            }} ref={tabsRef}>
                <Tabs
                    value={value}
                    onChange={handleChange}
                    indicatorColor="secondary"
                    textColor="inherit"
                    variant="fullWidth"
                    aria-label="full width tabs example"
                >
                    <Tab
                        label="Semua"
                        {...a11yProps(0)}
                        sx={{
                            color: value === 0 ? 'inherit' : 'white'
                        }}
                    />
                    <Tab
                        label="Hasil"
                        {...a11yProps(1)}
                        sx={{
                            color: value === 1 ? 'inherit' : 'white'
                        }}
                    />
                    <Tab
                        label="Penarikan"
                        {...a11yProps(2)}
                        sx={{
                            color: value === 2 ? 'inherit' : 'white'
                        }}
                    />
                </Tabs>
            </AppBar>
            <TabPanel value={value} index={0} dir={theme.direction}>
                {renderTable()}
            </TabPanel>
            <TabPanel value={value} index={1} dir={theme.direction}>
                {renderTable()}
            </TabPanel>
            <TabPanel value={value} index={2} dir={theme.direction}>
                {renderTable()}
            </TabPanel>
        </Box>
    );
}
